import { Hash, Images, MousePointerSquareDashed } from "lucide-react";

import { Badge } from "@runcheetah/ui";
import { cn } from "@/lib/utils";

import type { ScenePlan } from "../types";

export type CarouselSlideAssetLike = {
  generatedAssetId: string;
  blockId: string;
  role: string;
  position: number;
  objectKey: string;
  mimeType: string;
  width: number | null;
  height: number | null;
  prompt: string | null;
  signedUrl: string;
  createdAt: string;
};

export type CarouselReviewOutputLike = {
  format: {
    key: string;
    name: string;
    minSlides: number;
    maxSlides: number;
  };
  caption: string;
  hashtags: string[];
  slides: Array<{
    slide: {
      id: string;
      index: number;
      role: string;
      headline: string;
      body: string | null;
      cta: string | null;
    };
    scenePlan: { plan: ScenePlan } | null;
    assets: CarouselSlideAssetLike[];
  }>;
};

export type CarouselReviewState = {
  isCarousel: boolean;
  selectedSlideId: string | null;
  selectedSlide: CarouselReviewOutputLike["slides"][number] | null;
  selectedPlan: ScenePlan | null;
  selectedAssets: CarouselSlideAssetLike[];
  slideCount: number;
};

export const getCarouselReviewState = (
  carousel: CarouselReviewOutputLike | null | undefined,
  selectedSlideId: string | null,
): CarouselReviewState => {
  if (!carousel || carousel.slides.length === 0) {
    return {
      isCarousel: false,
      selectedSlideId: null,
      selectedSlide: null,
      selectedPlan: null,
      selectedAssets: [],
      slideCount: 0,
    };
  }

  const selectedSlide =
    carousel.slides.find((entry) => entry.slide.id === selectedSlideId) ?? carousel.slides[0]!;

  return {
    isCarousel: true,
    selectedSlideId: selectedSlide.slide.id,
    selectedSlide,
    selectedPlan: selectedSlide.scenePlan?.plan ?? null,
    selectedAssets: selectedSlide.assets,
    slideCount: carousel.slides.length,
  };
};

export function CarouselSlideRail({
  carousel,
  selectedSlideId,
  onSelectSlideId,
  className,
}: {
  carousel: CarouselReviewOutputLike | null | undefined;
  selectedSlideId: string | null;
  onSelectSlideId: (slideId: string) => void;
  className?: string;
}) {
  const state = getCarouselReviewState(carousel, selectedSlideId);

  if (!carousel || !state.isCarousel) {
    return null;
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Images className="size-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Carousel Slides
          </span>
        </div>
        <Badge variant="outline" className="rounded-full bg-background text-[11px]">
          {carousel.format.name} · {carousel.slides.length} slides
        </Badge>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1" aria-label="Carousel slides">
        {carousel.slides.map((entry) => {
          const selected = entry.slide.id === state.selectedSlideId;
          const thumbnailAsset = entry.assets[0] ?? null;

          return (
            <button
              key={entry.slide.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onSelectSlideId(entry.slide.id)}
              className={cn(
                "w-28 shrink-0 rounded-xl border bg-background p-1.5 text-left shadow-xs transition hover:border-primary/40 hover:bg-primary/5",
                selected && "border-primary bg-primary/10 ring-2 ring-primary/20",
              )}
            >
              <span className="block aspect-square overflow-hidden rounded-lg bg-muted">
                {thumbnailAsset ? (
                  <img
                    src={thumbnailAsset.signedUrl}
                    alt={`Slide ${entry.slide.index} thumbnail`}
                    width={thumbnailAsset.width ?? 160}
                    height={thumbnailAsset.height ?? 160}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-2xl font-semibold text-muted-foreground">
                    {entry.slide.index}
                  </span>
                )}
              </span>
              <span className="mt-2 block truncate text-xs font-medium text-foreground">
                Slide {entry.slide.index}
              </span>
              <span className="block truncate text-[11px] capitalize text-muted-foreground">
                {entry.slide.role}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CarouselSlideSummary({
  carousel,
  selectedSlideId,
  className,
}: {
  carousel: CarouselReviewOutputLike | null | undefined;
  selectedSlideId: string | null;
  className?: string;
}) {
  const state = getCarouselReviewState(carousel, selectedSlideId);
  const selected = state.selectedSlide;

  if (!selected) {
    return null;
  }

  return (
    <div className={cn("rounded-2xl border bg-background/85 px-4 py-3 shadow-xs", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="rounded-full capitalize">
          Slide {selected.slide.index} · {selected.slide.role}
        </Badge>
        {selected.scenePlan ? (
          <span className="text-xs text-muted-foreground">Scene plan ready</span>
        ) : (
          <span className="text-xs text-amber-700">No scene plan stored for this slide</span>
        )}
      </div>
      <h3 className="mt-2 text-sm font-semibold leading-6 text-foreground">
        {selected.slide.headline}
      </h3>
      {selected.slide.body ? (
        <p className="mt-1 text-sm leading-6 text-muted-foreground">{selected.slide.body}</p>
      ) : null}
      {selected.slide.cta ? (
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
          <MousePointerSquareDashed className="size-3.5" aria-hidden="true" />
          {selected.slide.cta}
        </p>
      ) : null}
    </div>
  );
}

export function CarouselCopyPanel({
  carousel,
  className,
}: {
  carousel: CarouselReviewOutputLike | null | undefined;
  className?: string;
}) {
  if (!carousel) {
    return null;
  }

  return (
    <div className={cn("space-y-3 rounded-2xl border bg-background/85 p-4 shadow-xs", className)}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        <Hash className="size-4" aria-hidden="true" />
        Caption
      </div>
      <p className="text-sm leading-6 text-foreground/78">{carousel.caption}</p>
      {carousel.hashtags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {carousel.hashtags.map((hashtag) => (
            <span
              key={hashtag}
              className="rounded-full bg-muted px-2 py-1 font-mono text-[11px] text-muted-foreground"
            >
              #{hashtag.replace(/^#/, "")}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
